import {Actionable, ActionableList, ACTIONABLES, IActionable, IActionableList, OActionableInit, OActionableListInit} from "/@back@/commons/actionables.js";
import {BaseElement, BASIS, Clickable, IEltRefreshable, OClickableInit, OSkinableInit, PickInit} from "/@back@/commons/basis.js";
import {IPopupable, IPopupCloseEvent, ORelativePosition} from "/@back@/commons/widgets/popupable.js";
import {PopupActions} from "/@back@/commons/widgets/popups.js";
import {ACTION, ActionWrapper, EButtonUiContext, IAction, isAccelKeyMgrPointer,} from "/@lib@/commons/actions.js";
import {IReg, REG} from "/@lib@/commons/registry.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {OBarActionsInit} from "/@back@/commons/widgets/bars.js";

/**
 * c-button : bouton simple (action à programmer)
 * c-button-toggle : bouton à 2 ou 3 positions (action à programmer)
 * c-button-actions : bouton qui affiche en popup-menu une liste d'actions.
 *
 * c-action : bouton piloté par une action qui se mue en bouton simple, toggle ou menu en fonction du type de l'action.
 * c-action-actions : regroupement d'une action principale avec un menu d'actions secondaires.
 * c-bar-actions : Barre de boutons issue d'une liste d'actions.
 */


/**
 * c-button : bouton simple.
 * Le script sur le onclick est spécifié par programmation.
 *
 * Attributes : cf Clickable +
 * Config attributes: role, ui-context
 *
 *	Class :
 *	"submit" pour les boutons : mise en exergue d'un bouton principal de validation (dans un dialog)
 */
export interface Button extends Clickable {
	initialize(init?: OButtonInit): this;
}

export type EButtonAriaRole = 'button' | 'menu' | 'menuitem' | 'spinbutton' | 'tab' | string;

/**
 * Configuration d'un bouton : role aria et contexte graphique permettant d'adapter les règles d'affichage.
 */
export interface OButtonInit extends OClickableInit {
	role?: EButtonAriaRole
	uiContext?: EButtonUiContext
}

export class Button extends Clickable {

	get role(): EButtonAriaRole {return this.getAttribute('role')}

	get uiContext(): EButtonUiContext {return this.getAttribute('ui-context')}

	protected _initialize(init: OButtonInit) {
		super._initialize(init);
		initButtonFromConfig(this, init);
	}

	protected _initSkin() {}
}

function initButtonFromConfig(elt: BaseElement & { uiContext?: EButtonUiContext }, init: OButtonInit) {
	if (init.role) DOM.setAttr(elt, 'role', init.role);
	else if (!elt.hasAttribute('role')) elt.setAttribute('role', 'button');
	if (init.uiContext) DOM.setAttr(elt, 'ui-context', init.uiContext);
	if (elt.shadowRoot) elt._initAndInstallSkin(elt.uiContext ? elt.uiContext + '/c-button' : 'bar/c-button', init);
}

REG.reg.registerSkin('dialog/c-button', 1, /* language=CSS */ `
	:host {
		display: flex;
		min-width: 0;
		min-height: min-content;
		max-height: 5em;
		overflow: hidden;
		align-items: center;
		text-align: center;
		justify-content: center;
		border: 1px solid var(--border-color);
		border-radius: 3px;
		padding: 0.3ex 0.6ex;
		margin: .5em;
		user-select: none;
	}

	:host([hidden]) {
		display: none;
	}

	:host([disabled]) {
		opacity: .3;
	}

	:host(:not([disabled])) {
		cursor: pointer;
	}

	:host(.vertical) {
		flex-direction: column;
	}

	:host(.inline) {
		display: inline-flex;
	}

	:host(.default:not([disabled])) {
		/*outline: 1px solid var(--border-color);
		outline-offset: -2px;*/
		color: var(--inv-color);
		background-color: var(--inv-bgcolor);
	}

	.icon {
		height: var(--icon-size);
		width: var(--icon-size);
		min-height: var(--icon-size);
		min-width: var(--icon-size);
		background: var(--icon-color) var(--icon-img) no-repeat center / contain;
		filter: var(--filter);
		/*mask: var(--icon-img) no-repeat center / contain;*/
		/*-webkit-mask: var(--icon-img) no-repeat center / contain;*/
		/*background-blend-mode: multiply;*/
	}

	:host(:hover:not([disabled])) > .icon {
		filter: var(--hover-filter);
	}

	:host(:active:not([disabled])) > .icon {
		filter: var(--actve-filter);
	}

	.icon:not([hidden]) + .label {
		-webkit-margin-start: 0.3em;
		margin-inline-start: 0.3em;
	}

	:host(.vertical) .icon {
		height: calc(var(--icon-size) * 1.5);
		width: calc(var(--icon-size) * 1.5);
	}

	.label {
		/* Utilisé seulement si white-space: nowrap; */
		overflow: hidden;
		text-overflow: ellipsis;
	}

	:host(:hover:not([disabled])) {
		/*box-shadow: inset 0 0 3px var(--border-color);*/
		background-color: var(--pressed-bgcolor);
	}

	:host(.default:hover:not([disabled])) {
		background-color: var(--inv-bgcolor);
		background-image: linear-gradient(var(--inv-pressed-bgcolor), var(--inv-pressed-bgcolor));
	}

	:host(:focus-visible) {
		outline: var(--focus-outline);
		outline-offset: -2px;
	}

	:host(:active:not([disabled])) {
		background-color: var(--pressed-bgcolor);
		/*box-shadow: inset 0 0 2px var(--border-color);*/
	}

	:host([role=menu]) {
		padding-inline-end: 1.2em;
		background: var(--dropdown-url) no-repeat right / 1em;
	}

	:host([role=menu]:hover:not([disabled])) {
		background: var(--pressed-bgcolor) var(--dropdown-url) no-repeat right / 1em;
	}

	:host([aria-pressed])::before {
		content: '\u2713\u00A0';
	}

	:host([aria-pressed=true]) {
		border: 1px inset var(--border-color);
	}

	:host([aria-pressed=false])::before {
		color: transparent;
	}

	:host([aria-pressed=mixed])::before {
		color: var(--fade-color);
	}


`);

REG.reg.registerSkin('bar/c-button', 1, /* language=CSS */ `
	:host {
		font-size: var(--label-size);
		border: solid 1px transparent;
		border-radius: 3px;
	  display: flex;
	  min-height: 0;
	  min-width: 0;
	  align-items: center;
	  text-align: center;
	  justify-content: center;
	  -moz-user-select: none;
	  -ms-user-select: none;
	  user-select: none;
  }

  :host([ellipsis]) {
	  overflow: hidden;
	  white-space: nowrap;
	  text-overflow: ellipsis;
  }

  :host(:not([hidden])) {
	  padding: 2px 4px;
  }

  :host([hidden]) {
	  display: none;
  }

  :host(:not([disabled])) {
		cursor: pointer;
	}

	:host([disabled]) {
		opacity: .5;
	}

	:host(.vertical) {
		flex-direction: column;
	}

	:host(.inline) {
		display: inline-flex;
	}

	.icon {
		height: var(--icon-size);
		width: var(--icon-size);
		min-height: var(--icon-size);
		min-width: var(--icon-size);
		background: var(--icon-color) var(--icon-img) no-repeat center / contain;
		filter: var(--filter);
		/*mask: var(--icon-img) no-repeat center / contain;*/
		/*-webkit-mask: var(--icon-img) no-repeat center / contain;*/
		/*background-blend-mode: multiply;*/
	}

	:host(:hover:not([disabled])) > .icon {
		filter: var(--hover-filter);
	}

	:host(:active:not([disabled])) > .icon {
		filter: var(--active-filter);
	}

	.icon:not([hidden]) + .label {
		-webkit-margin-start: 0.3em;
		margin-inline-start: 0.3em;
	}

	:host(.vertical) > .icon {
		height: calc(var(--icon-size) * 1.5);
		width: calc(var(--icon-size) * 1.5);
	}

	.label {
		/* Utilisé seulement si white-space: nowrap; */
		overflow: hidden;
		text-overflow: ellipsis;
	}

	:host(:hover:not([disabled])) {
		/*box-shadow: inset 0 0 3px var(--border-color);*/
		background: var(--pressed-bgcolor);
	}

	:host(:focus) {
		outline: var(--focus-outline);
		outline-offset: -2px;
	}

	:host(:active:not([disabled])) {
		background-color: var(--pressed-bgcolor);
		/*box-shadow: inset 0 0 3px var(--border-color);*/
	}

	:host([role=menu]:not([disabled])) { /* NE PAS SUPPR :not([disabled]) pour priorité > :host(:hover:not([disabled])) */
		padding-inline-end: 1.2em;
		background: var(--dropdown-url) no-repeat right / 1em;
	}

	:host([role=menu]:hover:not([disabled])) {
		background-color: var(--pressed-bgcolor);
	}

	:host([role=menu][disabled]) { /* NE PAS SUPPR [disabled] pour priorité > :host(:hover:not([disabled])) */
		padding-inline-end: 1.2em;
	}

	:host([aria-pressed]) {
		border: 1px outset var(--border-color);
		border-radius: 3px;
		margin-inline: 1px;
	}

	/* TODO aria-pressed=mixed */
	:host([aria-pressed=true]) {
		border: 1px inset;
		background-color: var(--pressed-bgcolor);
	}

	/*:host([aria-pressed])::before {
		content:'\u2713\u00A0';
	}

	:host(:not([aria-pressed=true]))::before {
		color:transparent;
	}*/
`);

REG.reg.registerSkin('menu/c-button', 1, /* language=CSS */ `
	:host {
		padding: .3ex;
		padding-inline-start: 0;
		padding-inline-end: .3em;
		display: grid;
		min-height: min-content;
		min-width: min-content;
		grid-template-areas: "check icon label sub";
		grid-template-columns: 0 var(--icon-size) 1fr auto;
		column-gap: .2em;
		-moz-user-select: none;
		user-select: none;
		cursor: default;
	}

	:host([hidden]) {
		display: none;
	}

	:host([disabled]) {
		opacity: 0.6;
	}

	.icon {
		grid-area: icon;
		background: var(--icon-color) var(--icon-img) no-repeat center / contain;
		background-origin: content-box;
		width: var(--icon-size);
		filter: var(--filter);
	}

	:host(:hover:not([disabled])) > .icon {
		filter: var(--hover-filter);
	}

	.label {
		grid-area: label;
		white-space: nowrap;
	}

	.ctn {
		grid-area: label;
		display: flex;
		min-height: 0;
		min-width: 0;
		align-items: center;
	}

	.ctn > .label {
		flex: 1;
	}

	.accel {
		padding-inline-start: .3em;
		font-style: italic;
		font-size: 80%;
		color: grey;
	}

	:host(:focus) {
		background-color: var(--row-inSel-bgcolor);
		outline: none;
	}

	:host(.inSubMenu) {
		background-color: var(--row-inSel-unfocus-bgcolor);
	}

	:host([aria-haspopup]):after {
		grid-area: sub;
		content: '';
		padding-inline-start: 1em;
		background: var(--dropdown-url) no-repeat center / 1em;
		transform: rotate(-90deg);
	}

	:host([aria-pressed]),
	:host([check-space]) {
		padding-inline-start: .3em;
		grid-template-columns: 1em var(--icon-size) 1fr auto;
	}

	:host([aria-pressed])::before {
		grid-area: check;
		content: ' ';
	}

	:host([aria-pressed=true])::before {
		color: transparent;
		background-position: center;
		background-repeat: no-repeat;
		background-image: var(--row-current-img-end);
	}

	:host([aria-pressed=mixed])::before {
		color: var(--fade-color);
		content: '?';
	}
`);

REG.reg.registerSkin('custom/c-button', 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: 0;
		min-width: 0;
		align-items: center;
		text-align: center;
		justify-content: center;
		-moz-user-select: none;
		user-select: none;
	}

	:host([hidden]) {
		display: none;
	}

	:host([disabled]) {
		opacity: 0.2;
	}

	:host(:not([disabled])) {
		cursor: pointer;
	}

	:host(.vertical) {
		flex-direction: column;
	}

	.icon {
		height: var(--icon-size);
		width: var(--icon-size);
		min-height: var(--icon-size);
		min-width: var(--icon-size);
		background: var(--icon-color) var(--icon-img) no-repeat center / contain;
		filter: var(--filter);
		/*mask: var(--icon-img) no-repeat center / contain;*/
		/*-webkit-mask: var(--icon-img) no-repeat center / contain;*/
		/*background-blend-mode: multiply;*/
	}

	:host(:hover:not([disabled])) > .icon {
		filter: var(--hover-filter);
	}

	:host(.vertical) .icon {
		height: calc(var(--icon-size) * 1.5);
		width: calc(var(--icon-size) * 1.5);
	}

	:host(:active:not([disabled])) > .icon {
		filter: var(--active-filter);
	}

	.label {
		flex: 1;
		/* Utilisé seulement si white-space: nowrap; */
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.icon:not([hidden]) + .label {
		margin-inline-start: .3em;
	}

	:host(:hover:not([disabled])) {
		/*box-shadow: inset 0 0 3px var(--border-color);*/
		background: var(--pressed-bgcolor);
	}

	:host(:focus-visible) {
		outline: var(--focus-outline);
		outline-offset: -2px;
	}

	:host(:active:not([disabled])) {
		background-color: var(--pressed-bgcolor);
		/*box-shadow: inset 0 0 2px var(--border-color);*/
	}

`);

customElements.define('c-button', Button);


/**
 * Bouton à double (voir triple) état.
 *
 * Attributes : cf Button +
 * State updatable attribute : aria-pressed (="true" | "false" | "mixed")
 */
export interface ButtonToggle extends Button {
	initialize(init?: OButtonToggleInit): this;
}

export interface OButtonToggleInit extends OButtonInit {
	toggleOn?: boolean /*on/off*/ | null /*mixed*/
}

export class ButtonToggle extends Button {
	get toggleOn(): boolean | null /* null pour 'mixed' */ | undefined /*pas encore init*/ {
		const val = this.getAttribute('aria-pressed');
		return val === 'true' ? true : val === 'false' ? false : val === 'mixed' ? null : undefined;
	}

	set toggleOn(val: boolean | null | undefined) {
		DOM.setAttr(this, 'aria-pressed', val != null ? val.toString() : val === undefined ? null : 'mixed');
		this.refresh();
	}

	_initialize(init: OButtonToggleInit) {
		super._initialize(init);
		if (init.toggleOn !== undefined) {
			//true / false / null(mixed)
			this.toggleOn = init.toggleOn;
		} else if (!this.hasAttribute('aria-pressed')) {
			//On impose l'état off si pas initialisé.
			this.toggleOn = false;
		}
	}
}

customElements.define('c-button-toggle', ButtonToggle);


//***************************************** ACTIONS *******************************************
/**
 * c-action : bouton piloté par une Action.
 */
export interface ActionBtn<C> extends Actionable<C> {
	initialize(init?: OActionBtnInit<C>): this;
}

export interface OActionBtnInit<C> extends OActionableInit<C>, OButtonInit {
	accel?: string
}

export class ActionBtn<C> extends Actionable<C> {

	/** Construit un ActionBtn en privillégiant la construction 'custom' de l'action sur l'implémentation standard. */
	static buildButton<C>(action: IAction<C>, ctx: C, uiContext: EButtonUiContext, parent?: Element, noBuildCustom = false): IActionable<C> {
		if (!action?.isInstantiable(ctx)) return null;
		const r = !noBuildCustom ? action.buildCustomButton(ctx, uiContext, parent) as IActionable<C> : undefined;
		if (r === null) return null;
		return r || new ActionBtn<C>().initialize({
			reg: REG.findReg(parent, ctx),
			action: action,
			actionContext: ctx,
			skin: action.getSkin(ctx),
			skinOver: action.getSkinOver(ctx),
			uiContext: uiContext,
			role: uiContext === 'menu' ? 'menuitem' : 'button'
		});
	}

	static buildButtons<C>(actions: IAction<C>[], ctx: C, uiContext: EButtonUiContext, parent?: Element): IActionable<C>[] {
		const r = [];
		for (const a of actions) {
			const b = ActionBtn.buildButton(a, ctx, uiContext, parent);
			if (b) r.push(b);
		}
		return r;
	}


	/**
	 * Retourne une liste de boutons du cache du reg ou l'instancie pour fournir les boutons d'une toolbar partagée.
	 * ATTENTION: tous les appels doivent considérer un ctx suffisamment équivalent pour la construction de cette liste de btns.
	 */
	static mergeActionablesForBar<C>(reg: IReg<any>, groupsOrder: string, ctx: C, ...list: string[]): IActionable<C>[] {
		const id = "barActs:" + (list.length === 1 ? list[0] : list.join());
		let a = reg.cache.get(id) as IActionable<C>[];
		if (!a) {
			const actions = ACTION.injectSepByGroup(reg.mergeLists<IAction<C>>(...list), groupsOrder, ctx);
			a = ActionBtn.buildButtons<C>(actions, ctx, 'bar');
			reg.cache.set(id, a);
		}
		return a;
	}


	protected _accel: string;

	protected _accelElt: HTMLElement;

	protected _popupMenu: PopupActions<C> & IEltRefreshable;

	get role(): EButtonAriaRole {return this.getAttribute('role')}

	get uiContext(): EButtonUiContext {return this.getAttribute('ui-context')}

	get label(): string {
		if (this.hideLabel) return null;
		return super.label;
	}

	set label(val: string) {
		super.label = val;
	}

	get title(): string {
		if (this.hideLabel) return this._appendAccel(super.label || super.title);
		return this.uiContext === 'menu' ? super.title : this._appendAccel(super.title);
	}

	get ariaLabel(): string {
		return super.label || super.title;
	}

	/** Masquage du label : Par défaut, pas de label dans une barre d'actions si une icone est dispo. */
	_hideLabel: boolean | undefined;
	protected get hideLabel(): boolean {return this._hideLabel === undefined ? this.uiContext === 'bar' && !!this.icon : this._hideLabel}

	protected _appendAccel(text: string) {
		const accel = this.accel;
		return accel ? text ? `${text} (${accel})` : `(${accel})` : text || '';
	}

	set title(val: string) {
		super.title = val;
	}

	get accel(): string {
		if (this._accel != null) return this._accel;
		if (this._action && isAccelKeyMgrPointer(this._actionContext)) {
			return this._actionContext.accelKeyMgr.searchAccelText(this._action);
		}
		return null;
	}

	set accel(val: string) {
		if (this._accel != val) {
			this._accel = val;
			this.refresh();
		}
	}

	protected _initialize(init: OActionBtnInit<C>) {
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		sr.appendChild(document.createElement('slot'));
		initButtonFromConfig(this, init);
		super._initialize(init);
		if (init.accel) this._accel = init.accel;
		this.setAttribute('id', this._action.getId());
		if (this._action.isMenu(this._actionContext)) {
			if (this.role === 'menuitem') {
				//sous-menu
				this.setAttribute('aria-haspopup', 'true');
			} else {
				this.setAttribute('role', 'menu');
			}
		}
	}

	protected _refresh() {
		super._refresh();
		if (this.uiContext === 'menu') {
			const accel = this.accel;
			if (accel) {
				if (!this._accelElt) this._createAccel();
				DOM.setTextContent(this._accelElt, accel);
			} else if (this._accelElt) DOM.setTextContent(this._accelElt, null);
		}
	}

	/** création de l'accel à la demande. */
	protected _createAccel() {
		const labelParent = this.shadowRoot.appendChild(<span class='ctn'/>);
		if (!this._labelElt) this._createLabel();
		labelParent.appendChild(this._labelElt);
		this._accelElt = labelParent.appendChild(<span class='accel'/>);
	}

	protected _executeAction(ev: Event) {
		if (this._action && this._action.isAvailable(this._actionContext)) {
			if (this._action.isMenu(this._actionContext)) {
				//On affiche le menu contextuel de l'action.
				const actions = this._action.getDatas('menu', this._actionContext);
				if (!this._popupMenu) {
					this._popupMenu = this._createPopup({actions, actionContext: this._actionContext});
					this._popupMenu.addEventListener('c-beforeclose', (ev: CustomEvent) => {
						if (ev.target === this._popupMenu) {
							this.classList.remove('inSubMenu');
						}
					});
				} else {
					if (this._popupMenu.contains(ev.target as Node)) return;
					this._popupMenu.actions = actions;
				}
				this.classList.add('inSubMenu');
				this._popupMenu.show(this.uiContext !== 'menu' ? this : {
					//on le place à doite
					posFrom: this,
					fromX: 'end',
					marginX: -5,
					toleranceY: Number.MAX_SAFE_INTEGER,
					notAvailableSpace: {
						//sinon, on le place à gauche en autorisant la superposition.
						posFrom: this,
						targetX: 'end',
						marginX: 5,
						toleranceY: Number.MAX_SAFE_INTEGER,
						toleranceX: Number.MAX_SAFE_INTEGER
					} as ORelativePosition
				} as ORelativePosition, this, this);
				ev.stopImmediatePropagation();
				ev.preventDefault();
			} else {
				//On execute l'action.
				//.executeIfAvailable() NON déjà évalué en amont et pb pour les actions dont l'activation est async et forcé au niveau du button (cf paste bookmark par ex.).
				this._action.execute(this._actionContext, ev);
			}
		}
		this.dispatchEvent(new CustomEvent('c-actioned', {detail: {actionable: this}, bubbles: true, composed: true}));
	}

	protected _createPopup(init: OActionableListInit<C>): PopupActions<C> {
		return new PopupActions<C>().initialize(init);
	}

}

customElements.define('c-action', ActionBtn);


/**
 * Bouton affichant une liste d'actions en popup.
 *
 * Attributes : cf Button + ActionableList
 */
export interface ButtonActions<C> extends Button {
	initialize(init?: OButtonActionsInit<C>): this;
}

export interface OButtonActionsInit<C> extends OActionableListInit<C>, OButtonInit {}

export class ButtonActions<C> extends Button {
	protected _actions: IAction<C>[];
	protected _actionContext: C;
	protected _popup: IPopupable & IActionableList<C>;

	get actions(): IAction<C>[] {return this._actions}

	set actions(actions: IAction<C>[]) {
		this._actions = actions;
		if (this._popup) this._popup.actions = actions;
	}

	get actionContext(): C {return this._actionContext}

	get popup() {
		if (!this._popup) this._popup = this._createPopup();
		return this._popup;
	}

	protected _initialize(init: OButtonActionsInit<C>) {
		init.role = 'menu';
		this._actions = init.groupOrder == null ? init.actions : ACTION.injectSepByGroup(init.actions, init.groupOrder, this._actionContext);
		this._actionContext = init.actionContext || (this as any as C); //A défaut de contextAction on prend l'élément courant.
		super._initialize(init);
		if (!this.onclick) this.onclick = function (this: ButtonActions<C>, ev: MouseEvent) {
			if (!this.disabled && (!this._popup || !this._popup.contains(ev.target as Node))) this.openPopup();
		}
	}

	openPopup() {
		this.popup.show(this, this, this);
	}

	buildInitFromAtts(init?: OButtonActionsInit<C>): OButtonActionsInit<C> {
		init = super.buildInitFromAtts(init);
		init.actionContext = ACTIONABLES.extractActionContextFromDom(this, init);
		init.actions = ACTIONABLES.extractActionsFromDom(this, init);
		return init;
	}

	protected _createPopup(): IPopupable & IActionableList<C> {
		return this.appendChild(new PopupActions<C>().initialize({actions: this.actions, actionContext: this.actionContext}));
	}
}

customElements.define('c-button-actions', ButtonActions);


/**
 * tag: c-action-actions
 * Regroupement d'un c-action et d'un c-button-actions en un seul widget pour proposer des actions secondaires
 * à une action principale.
 * La config lastAsDefault permet de changer l'action principale par la dernière action sélectionnée dans le menu.
 *
 * Attributes : cf BaseElement +
 * Config attribute : ui-context, last-as-default
 * Config init attributes : action, actions, action-context
 * (todo Config attributes : action-skin, action-skin-over, actions-skin, actions-skin-over pour skins des actionBtn et actionsBtn ?)
 */
export interface ActionActions<C> extends BaseElement {
	initialize(init?: OActionActionsInit<C>): this
}

export interface OActionActionsInit<C> extends OSkinableInit, PickInit<ActionActions<C>, 'actionContext' | 'lastAsDefault' | 'uiContext'> {
	actionBtn?: OActionableInit<C>
	actionsBtn?: OActionableListInit<C>
}


export class ActionActions<C> extends BaseElement {
	actionBtn: ActionBtn<C>;
	actionsBtn: ButtonActions<C>;

	get uiContext(): EButtonUiContext {return this.getAttribute('ui-context')}

	get lastAsDefault(): boolean {return this.hasAttribute('last-as-default')}

	get actionContext(): C {return this.actionBtn.actionContext}

	constructor() {
		super();
		this.actionBtn = new ActionBtn();
		this.actionsBtn = new ButtonActions();
	}

	_initialize(init: OActionActionsInit<C>) {
		//configs locales
		if (init.uiContext) this.setAttribute('ui-context', init.uiContext);
		if (init.lastAsDefault) this.setAttribute('last-as-default', "");
		//config par défaut.
		if (!this.hasAttribute('ui-context')) this.setAttribute('ui-context', 'bar');

		//transfert des attributs aux sub-widgets.
		DOM.setAttr(this.actionBtn, 'action', BASIS.extractAttr(this, 'action'));
		DOM.setAttr(this.actionsBtn, 'actions', BASIS.extractAttr(this, 'actions'));
		const ctxSvc = BASIS.extractAttr(this, 'action-context');
		if (ctxSvc) {
			DOM.setAttr(this.actionBtn, 'action-context', ctxSvc);
			DOM.setAttr(this.actionsBtn, 'action-context', ctxSvc);
		}
		//ui-context
		if (!this.actionBtn.hasAttribute('ui-context')) this.actionBtn.setAttribute('ui-context', this.uiContext === 'bar' ? 'bar' : 'custom');
		if (!this.actionsBtn.hasAttribute('ui-context')) this.actionsBtn.setAttribute('ui-context', 'custom');

		//init des sous-éléments.
		const actionBtnInit = init.actionBtn ? Object.create(init.actionBtn) : this.actionBtn.buildInitFromAtts();
		if (init.actionContext) actionBtnInit.actionContext = init.actionContext;
		const actionsBtnInit = init.actionsBtn ? Object.create(init.actionsBtn) : this.actionsBtn.buildInitFromAtts();
		if (init.actionContext) actionsBtnInit.actionContext = init.actionContext;

		this.actionBtn.initialize(actionBtnInit);
		this.actionsBtn.initialize(actionsBtnInit);

		//construction du contenu du widget
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);
		sr.appendChild(this.actionBtn);
		sr.appendChild(this.actionsBtn);

		if (this.lastAsDefault) {
			this.actionsBtn.popup.addEventListener('c-close', (ev: IPopupCloseEvent) => {
				if (!ev.detail.returnValue) return;
				const newButton = new ActionBtn<C>().initialize({
					action: ev.detail.returnValue,
					actionContext: this.actionBtn.actionContext,
					uiContext: this.actionBtn.uiContext,
					skin: init.skin,
					skinOver: init.skinOver,
					tabIndexCustom: this.actionBtn.tabIndexCustom,
				} as OActionableInit<C> & OButtonInit);
				this.shadowRoot.insertBefore(newButton, this.actionBtn);
				this.actionBtn.remove();
				this.actionBtn = newButton;
			});
		}
	}

	protected _refresh() {
		super._refresh();
		this.actionBtn.refresh();
		this.actionsBtn.refresh();
	}
}

REG.reg.registerSkin('c-action-actions', 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: 0;
		min-width: 0;
		text-align: center;
		justify-content: center;
	}

	:host([ui-context=dialog]) {
		border: solid 1px var(--border-color);
		margin: .5em;
	}

	:host([ui-context=dialog]) > c-action {
		padding: 0.3ex 0.6ex;
		/* RTL border-inline-end: */
		border-inline-end: solid 1px var(--border-color);
	}

	:host([ui-context=dialog]) > c-action[aria-pressed]::before {
		grid-area: check;
		content: '\u2713';
	}

	:host([ui-context=dialog]) > c-action[aria-pressed=false]::before {
		color: transparent;
	}

	:host([ui-context=dialog]) > c-action[aria-pressed=mixed]::before {
		color: var(--fade-color);
	}

	c-action {
		flex: 1 1;
	}

	c-button-actions {
		background: var(--dropdown-url) no-repeat center / 1em;
		padding: .5em;
	}
`);

customElements.define('c-action-actions', ActionActions);


/**
 * ActionableList présentée sous forme de liste type radioBtn
 */
export class RadioBtnsActionable<C> extends ActionableList<C> {

	protected _initialize(init: OBarActionsInit<C>) {
		super._initialize(init);
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.nodeName, init);
		const ctn = sr.appendChild(<slot/>);
	}

	get disabled(): boolean {return this.hasAttribute('disabled')}

	set disabled(val: boolean) {
		if (DOM.setAttrBool(this, 'disabled', val)) this.refreshContent();
	}

	_createButton(action: IAction<C>): Element {
		const ctn = this;
		const ctx = this._actionContext;
		const buildBtn = (act: IAction<C>, prt: HTMLElement) => {
			//Sous-liste
			if (act.isMenu(ctx)) {
				if (act.isInstantiable(ctx)) {
					const subActions = act.getDatas("menu", ctx);
					if (subActions?.length > 0) {
						const src = act.getIcon(ctx);
						const listElt = <div class="radioBox">
							<div class="icoLabel">
								{src ? <img src={src} class="icon"/> : undefined}
								<span class="label">{act.getLabel(ctx)}</span>
							</div>
						</div>;
						const acionsElt = listElt.appendChild(<div class="actionsList"/>);
						const desc = act.getDescription(ctx);
						if (desc) listElt.title = desc;
						subActions.forEach((sub) => acionsElt.append(buildBtn(sub, listElt)));
						return listElt;
					}
				}
				return null;
			}
			//Checkbox
			return ActionBtn.buildButton(new ActionWrapper(act.getId()).setSkinOver("c-radios-actions-action").setOverridenSvc(act).override('isEnabled', function (this: ActionWrapper<C>, ctx: C) {
				if (ctn.disabled) return false;
				return this.getWrapped(ctx).isEnabled(ctx);
			}), ctx, ctn.uiContext, prt);
		}
		return buildBtn(action, this);
	}

}

customElements.define('c-radios-actions', RadioBtnsActionable);

REG.reg.registerSkin('c-radios-actions', 1, /* language=CSS */ `
	/* Séparateur */
	::slotted(hr) {
		display: none;
	}
`);

REG.reg.registerSkin('c-radios-actions-action', 1, /* language=CSS */ `
	:host([aria-pressed])::before,
	:host(.unknlownValue)::before {
		content: '';
		width: 1.5em;
	}

	:host([aria-pressed=true])::before,
	:host(.unknlownValue)::before {
		content: '✓';
	}

	:host(.unknlownValue) {
		color: var(--error-color);
	}

	:host(.unknlownValue[disabled]) {
		opacity: 1;
	}

`);